/**
 * Prepended to JSON `hints` so learners get interaction help when drag is flaky or unclear.
 * @param {boolean} touchTrue - from useTouchFirstPlacement (coarse pointer, no hover)
 * @param {{ canvasStyle?: boolean }} [opts] - canvas picture + tray vs chip + boxes
 * @returns {{ text: string, audioText: string, level: number }[]}
 */
export function buildDragDropMechanicHints(touchFirst, opts = {}) {
  const canvasStyle = !!opts.canvasStyle;

  if (touchFirst) {
    return [
      {
        text: canvasStyle
          ? 'Tap a piece below first — it gets a colored ring. Then tap the matching area on the picture.'
          : 'Tap a chip first — it will highlight. Then tap the box where it belongs.',
        audioText: canvasStyle
          ? 'Tap a piece below first — it gets a colored ring. Then tap the matching area on the picture.'
          : 'Tap a chip first — it will highlight. Then tap the box where it belongs.',
        level: 1,
      },
      {
        text: canvasStyle
          ? 'Tap any labeled zone to place your piece. Tap the piece in the zone to return it to the tray and try again.'
          : 'Tap an empty dashed box on the picture. If it does not match, tap a different box, or tap the chip again to re-select it.',
        audioText: canvasStyle
          ? 'Tap any labeled zone to place your piece. Tap the piece in the zone to return it to the tray and try again.'
          : 'Tap an empty dashed box on the picture. If it does not match, tap a different box, or tap the chip again to re-select it.',
        level: 2,
      },
    ];
  }

  return [
    {
      text: canvasStyle
        ? 'Drag a piece from the tray and drop it on the matching zone on the picture.'
        : 'Drag a chip onto the right box — or tap a chip, then tap the box where it should go.',
      audioText: canvasStyle
        ? 'Drag a piece from the tray and drop it on the matching zone on the picture.'
        : 'Drag a chip onto the right box — or tap a chip, then tap the box where it should go.',
      level: 1,
    },
    {
      text: canvasStyle
        ? 'If the piece snaps back to the tray, that spot was not its home — try a different zone.'
        : 'If dragging is tricky, use two taps: tap a chip, then tap the target box.',
      audioText: canvasStyle
        ? 'If the piece snaps back to the tray, that spot was not its home — try a different zone.'
        : 'If dragging is tricky, use two taps: tap a chip, then tap the target box.',
      level: 2,
    },
  ];
}

/**
 * Single combined “how to use” line for one-shot hint (then content-only progression).
 * @param {boolean} touchFirst
 * @param {{ canvasStyle?: boolean }} [opts]
 * @returns {{ text: string, audioText: string } | null}
 */
export function buildOneShotInteractionHint(touchFirst, opts = {}) {
  const parts = buildDragDropMechanicHints(touchFirst, opts);
  if (!parts.length) return null;
  return {
    text: parts.map((p) => p.text).join(' '),
    audioText: parts.map((p) => p.audioText).join(' '),
  };
}

/**
 * Guided-lesson MatchEngine (tap cards only) — mechanic wording lives in Help only.
 * @param {'diet'|'habitat'|'pattern'|'sequence'|'group'} [domain]
 * @returns {{ text: string, audioText: string }}
 */
export function buildMatchTapOneShotHint(domain = 'group') {
  if (domain === 'diet') {
    return {
      text: 'Read the clue, then tap the food group that fits.',
      audioText: 'Read the clue, then tap the food group that fits.',
    };
  }
  if (domain === 'habitat') {
    return {
      text: 'Read the clue, then tap the home that fits best.',
      audioText: 'Read the clue, then tap the home that fits best.',
    };
  }
  return {
    text: 'Read the clue, then tap the answer that fits best.',
    audioText: 'Read the clue, then tap the answer that fits best.',
  };
}

/**
 * Canvas guided rounds (one animal/item at a time, zone labels as tap buttons).
 * @param {'diet'|'habitat'|'pattern'|'sequence'|'group'} [domain]
 * @returns {{ text: string, audioText: string }}
 */
export function buildCanvasGuidedRoundsOneShotHint(domain = 'habitat') {
  if (domain === 'diet') {
    return {
      text: 'Look at the animal, then tap the food group that fits best.',
      audioText: 'Look at the animal, then tap the food group that fits best.',
    };
  }
  return {
    text: 'Think about what this animal needs, then tap the habitat that fits best.',
    audioText: 'Think about what this animal needs, then tap the habitat that fits best.',
  };
}
